import React, { useState, useContext, useCallback } from 'react';
import { AdminThemeContext, getAdminStyles } from './adminTheme';

// Client Search (2026-07-22): search any client by UCC / Mobile / Email /
// BO ID / PAN, then drill into full detail including the new 4-state
// (Active/Reactive/Suspend/Closed) segment status from client_segment_status
// (see client_segment_status.migration.sql). Display-only in this round --
// no controls here change a client's segment status.

const STATUS_COLORS = {
  '1': { bg: '#f0fdf4', col: '#15803d', bdr: '#bbf7d0' }, // Active
  '2': { bg: '#eff6ff', col: '#1d4ed8', bdr: '#bfdbfe' }, // Inactive (backend: REACTIVE)
  '3': { bg: '#fffbeb', col: '#b45309', bdr: '#fde68a' }, // Suspended
  '4': { bg: '#fef2f2', col: '#b91c1c', bdr: '#fecaca' }, // Closed
};

// Display-only wording maps. Backend keeps sending numeric codes
// (account_status: 1/2/3, segment_status.status: 1/2/3/4) -- these just
// translate the code to a word for the admin UI. No backend change.
const ACCOUNT_STATUS_LABELS = { '1': 'Active', '2': 'Suspended', '3': 'Closed' };
const SEGMENT_STATUS_LABELS = { '1': 'Active', '2': 'Inactive', '3': 'Suspended', '4': 'Closed' };
const fmtAccountStatus = (v) => ACCOUNT_STATUS_LABELS[String(v)] || (v ?? '—');
const fmtSegmentStatus = (v) => SEGMENT_STATUS_LABELS[String(v)] || (v ?? '—');

const SEARCH_FIELDS = [
  { key: 'ucc',    label: 'UCC'    },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email',  label: 'Email'  },
  { key: 'bo_id',  label: 'BO ID'  },
  { key: 'pan',    label: 'PAN'    },
];

export default function ClientSearch() {
  const { T } = useContext(AdminThemeContext);
  const S = getAdminStyles(T);

  const [form,          setForm]          = useState({ ucc: '', mobile: '', email: '', bo_id: '', pan: '' });
  const [results,       setResults]       = useState(null);
  const [searching,     setSearching]     = useState(false);
  const [selected,      setSelected]      = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error,         setError]         = useState('');

  const token = () => localStorage.getItem('nb_admin_token');

  // UCC (2026-07-22): UCC codes are always exactly 8 characters, so the
  // search field is capped to 8 and any non-alphanumeric characters are
  // stripped as the user types. Other fields (Mobile/Email/BO ID/PAN) are
  // untouched -- this restriction applies only to the UCC box.
  const handleChange = (key, val) => {
    if (key === 'ucc') {
      val = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
    }
    setForm(f => ({ ...f, [key]: val }));
  };

  // Parses a fetch Response as JSON, but first checks the content-type so a
  // non-JSON response (e.g. the SPA's index.html served for an unmatched
  // route, which happens if the backend hasn't picked up a new endpoint
  // yet) produces a clear, actionable error instead of the cryptic
  // "Unexpected token '<' ... is not valid JSON".
  const parseJsonResponse = async (res) => {
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      throw new Error('Server did not return JSON (got ' + (ct || 'unknown content-type') + '). The backend may not have been redeployed with this endpoint yet.');
    }
    return res.json();
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const filled = Object.entries(form).filter(([, v]) => v.trim());
    if (filled.length === 0) {
      setError('Enter at least one field to search.');
      return;
    }
    setError('');
    setSearching(true);
    setSelected(null);
    setDetail(null);
    try {
      const params = new URLSearchParams();
      filled.forEach(([k, v]) => params.set(k, v.trim()));
      const res  = await fetch(`/api/admin/clients/search?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) { setError(data.error || 'Search failed.'); setResults(null); return; }
      setResults(data.clients || []);
    } catch (err) {
      setError('Search failed: ' + err.message);
      setResults(null);
    } finally {
      setSearching(false);
    }
  };

  const handleClear = () => {
    setForm({ ucc: '', mobile: '', email: '', bo_id: '', pan: '' });
    setResults(null);
    setSelected(null);
    setDetail(null);
    setError('');
  };

  const openDetail = useCallback(async (ucc) => {
    setSelected(ucc);
    setLoadingDetail(true);
    setDetail(null);
    setError('');
    try {
      const res  = await fetch(`/api/admin/clients/${encodeURIComponent(ucc)}/detail`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) { setError(data.error || 'Failed to load client.'); return; }
      setDetail(data);
    } catch (err) {
      setError('Failed to load client: ' + err.message);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const inputStyle = {
    fontSize: 12, padding: '7px 10px', borderRadius: 6,
    border: `0.5px solid ${T.bdr}`, background: T.surf, color: T.tx,
    fontFamily: "'DM Sans',sans-serif", width: '100%', boxSizing: 'border-box',
  };

  return (
    <div>
      {/* Search form */}
      <div style={{ ...S.card, padding: 20, marginBottom: 16 }}>
        <form onSubmit={handleSearch}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 14 }}>
            {SEARCH_FIELDS.map(f => (
              <div key={f.key}>
                <div style={{ fontSize: 10, color: T.tx2, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: .4 }}>{f.label}</div>
                <input
                  style={inputStyle}
                  value={form[f.key]}
                  onChange={e => handleChange(f.key, e.target.value)}
                  placeholder={`Search by ${f.label}`}
                  maxLength={f.key === 'ucc' ? 8 : undefined}
                />
              </div>
            ))}
          </div>
          {error && <div style={{ fontSize: 12, color: T.err, marginBottom: 10 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" disabled={searching}
              style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: T.brand, color: '#fff', fontSize: 12, fontWeight: 600, cursor: searching ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans',sans-serif", opacity: searching ? .6 : 1 }}>
              {searching ? 'Searching...' : 'Search'}
            </button>
            <button type="button" onClick={handleClear} style={{ ...S.btnGhost, fontSize: 12, padding: '8px 16px' }}>Clear</button>
          </div>
        </form>
      </div>

      {/* Results list */}
      {results !== null && !selected && (
        <div style={{ ...S.card }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1fr 1.4fr 1fr 90px', padding: '9px 20px', background: T.surf2, borderBottom: `0.5px solid ${T.bdr}` }}>
            {['UCC', 'Name', 'Mobile', 'Email', 'Account Status', ''].map(h => (
              <div key={h} style={{ fontSize: 10, color: T.tx2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: .4 }}>{h}</div>
            ))}
          </div>
          {results.length === 0 && <div style={{ ...S.empty }}>No clients matched your search.</div>}
          {results.map((c, i) => (
            <div key={c.client_id} onClick={() => openDetail(c.ucc)}
              style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1fr 1.4fr 1fr 90px', padding: '12px 20px', alignItems: 'center', borderBottom: i < results.length - 1 ? `0.5px solid ${T.bdr}` : 'none', cursor: 'pointer' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.tx }}>{c.ucc}</div>
              <div style={{ fontSize: 12, color: T.tx }}>{c.client_name}</div>
              <div style={{ fontSize: 12, color: T.tx2 }}>{c.mobile}</div>
              <div style={{ fontSize: 12, color: T.tx2 }}>{c.email}</div>
              <div style={{ fontSize: 11, color: c.is_active ? T.ok : T.err }}>{fmtAccountStatus(c.account_status)}</div>
              <div style={{ fontSize: 11, color: T.brand, fontWeight: 600 }}>View →</div>
            </div>
          ))}
        </div>
      )}

      {/* Detail view */}
      {selected && (
        <div>
          <button onClick={() => { setSelected(null); setDetail(null); }} style={{ ...S.btnGhost, fontSize: 12, padding: '6px 14px', marginBottom: 14 }}>← Back to results</button>

          {error && <div style={{ fontSize: 12, color: T.err, marginBottom: 10 }}>{error}</div>}
          {loadingDetail && <div style={{ ...S.empty }}>Loading client...</div>}

          {detail && (
            <>
              <div style={{ ...S.card, padding: 20, marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.tx, marginBottom: 4 }}>{detail.client.client_name}</div>
                <div style={{ fontSize: 12, color: T.tx2, marginBottom: 14 }}>UCC: {detail.client.ucc} · {fmtAccountStatus(detail.client.account_status)}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
                  {[
                    ['Mobile',   detail.client.mobile],
                    ['Email',    detail.client.email],
                    ['PAN',      detail.client.pan     || '—'],
                    ['BO ID',    detail.client.bo_id    || '—'],
                    ['DP ID',    detail.client.dp_id    || '—'],
                    ['Terminal', detail.client.terminal || '—'],
                    ['City',     detail.client.city     || '—'],
                    ['State',    detail.client.state    || '—'],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize: 10, color: T.tx3, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 12, color: T.tx }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ ...S.card }}>
                <div style={{ padding: '14px 20px', borderBottom: `0.5px solid ${T.bdr}`, fontSize: 13, fontWeight: 600, color: T.tx }}>Segment Status</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.5fr', padding: '9px 20px', background: T.surf2, borderBottom: `0.5px solid ${T.bdr}` }}>
                  {['Exchange', 'Segment', 'Status', 'Last Updated'].map(h => (
                    <div key={h} style={{ fontSize: 10, color: T.tx2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: .4 }}>{h}</div>
                  ))}
                </div>
                {(detail.segment_status || []).map((s, i) => {
                  const c = STATUS_COLORS[String(s.status)] || STATUS_COLORS['4'];
                  return (
                    <div key={`${s.exchange}-${s.segment}`}
                      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.5fr', padding: '12px 20px', alignItems: 'center', borderBottom: i < detail.segment_status.length - 1 ? `0.5px solid ${T.bdr}` : 'none' }}>
                      <div style={{ fontSize: 12, color: T.tx }}>{s.exchange}</div>
                      <div style={{ fontSize: 12, color: T.tx }}>{s.segment}</div>
                      <div><span style={{ ...S.badge(c.bg, c.col, c.bdr) }}>{fmtSegmentStatus(s.status)}</span></div>
                      <div style={{ fontSize: 11, color: T.tx2 }}>
                        {s.updated_at ? new Date(s.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </div>
                    </div>
                  );
                })}
                {(!detail.segment_status || detail.segment_status.length === 0) && (
                  <div style={{ ...S.empty }}>No segment status records for this client yet.</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}